import { Directive, ElementRef, Inject, Input, NgZone, OnInit, OnDestroy, PLATFORM_ID, output, EventEmitter, input, inject } from '@angular/core';
import {UntypedFormControl, Validators} from '@angular/forms';
import {MatValidateAddressCustomDirective} from './mat-address-validator.directive';
import {DynamicScriptLoaderService} from 'src/app/core/services/dynamic-script-loader-service';
import {isPlatformBrowser} from '@angular/common';
import { Subject, fromEvent } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import PlaceResult = google.maps.places.PlaceResult;
import AutocompleteOptions = google.maps.places.AutocompleteOptions;
import AutocompletePrediction = google.maps.places.AutocompletePrediction;

@Directive({
    selector: '[pghMatGoogleMapsAutocompleteCustom]',
    exportAs: 'pghMatGoogleMapsAutocompleteCustom',
    standalone: true,
})
export class MatGoogleMapsAutocompleteCustomDirective implements OnInit, OnDestroy {

  @Input()
  address: PlaceResult | string;

  readonly country = input<string | string[]>(undefined);

  readonly placeIdOnly = input<boolean>(undefined);

  readonly strictBounds = input<boolean>(undefined);

  readonly types = input<string[]>(undefined);

  readonly type = input<string>(undefined);

  @Input()
  autoCompleteOptions: AutocompleteOptions = {};

  readonly onChange = output<PlaceResult | string | null>();

  readonly onAutocompleteSelected = output<PlaceResult>();

  readonly onLocationSelected = output<Location>();

  readonly onPredictions = output<AutocompletePrediction[]>();

  private autocompleteInstance: google.maps.places.Autocomplete | null = null;
  private destroy$ = new Subject<void>();
  private onNewPlaceResult: EventEmitter<any> = new EventEmitter();
  private addressValidator: MatValidateAddressCustomDirective = new MatValidateAddressCustomDirective();
  private scriptLoader = inject(DynamicScriptLoaderService);

  public addressSearchControl: UntypedFormControl = new UntypedFormControl({value: null}, Validators.compose([
    Validators.required,
    this.addressValidator.validate()])
  );

  constructor(@Inject(PLATFORM_ID) public platformId: string,
              public elemRef: ElementRef,
              private ngZone: NgZone) {
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.autocompleteInstance) {
      google.maps.event.clearInstanceListeners(this.autocompleteInstance);
    }
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.addressValidator.subscribe(this.onNewPlaceResult);
      const options: AutocompleteOptions = {
        // types: ['address'],
        // componentRestrictions: {country: this.country},
        placeIdOnly: this.placeIdOnly(),
        strictBounds: this.strictBounds(),
        // types: this.types,
        type: this.type()
      };

      const country = this.country();
      // tslint:disable-next-line:no-unused-expression
      country ? options.componentRestrictions = {country: country} : null;
      // tslint:disable-next-line:no-unused-expression
      country ? options.types = this.types() : null;

      this.autoCompleteOptions = Object.assign(this.autoCompleteOptions, options);
      this.initGoogleMapsAutocomplete();
    }

  }

  public initGoogleMapsAutocomplete(): void {

    this.scriptLoader.load('google-maps-api')
      .then(() => {
        this.autocompleteInstance = new google.maps.places.Autocomplete(this.elemRef.nativeElement, this.autoCompleteOptions);
        const autocompleteService = new google.maps.places.AutocompleteService();

        this.autocompleteInstance.addListener('place_changed', () => {
          this.ngZone.run(() => {
            // get the place result
            const place: PlaceResult = this.autocompleteInstance!.getPlace();

            if (!place.place_id || place.geometry === undefined || place.geometry === null) {
              // place result is not valid
              this.onChange.emit(null);
              return;
            }
            this.address = place.formatted_address;
            this.onAutocompleteSelected.emit(place);
          });
        });

        fromEvent<Event>(this.elemRef.nativeElement, 'input')
          .pipe(
            debounceTime(300),
            takeUntil(this.destroy$)
          )
          .subscribe((event: Event) => {
            const value = (event.target as HTMLInputElement).value;
            if (!value) {
              this.onPredictions.emit([]);
              return;
            }
            autocompleteService.getPlacePredictions(
              { input: value, ...this.autoCompleteOptions },
              (predictions, status) => {
                this.ngZone.run(() => {
                  if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
                    this.onPredictions.emit(predictions);
                  } else {
                    this.onPredictions.emit([]);
                  }
                });
              }
            );
          });
      })
      .catch((err) => console.error(err));

  }

}
